import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const SCOPE = [
  { icon: "ri-newspaper-line", title: "影视资讯", desc: "追踪剧集、电影与网络剧的最新动态，帮助你在第一时间了解正在被讨论的作品。" },
  { icon: "ri-book-open-line", title: "剧情介绍", desc: "分区分段梳理剧情脉络，把复杂的时间线与人物关系讲清楚，降低理解门槛。" },
  { icon: "ri-user-star-line", title: "角色分析", desc: "剖析角色的背景、动机与成长弧光，读懂人物在剧情中真正承担的作用。" },
  { icon: "ri-lightbulb-flash-line", title: "深度专题", desc: "针对热门作品推出剧情复盘、细节考据与主创对话等专题内容。" }
];

const TEAM = [
  { name: "青崖", role: "主编 · 剧情解析", desc: "偏爱结构复杂的长线叙事，负责剧情复盘与时间线梳理。" },
  { name: "南屿", role: "影评人 · 行业观察", desc: "关注影视行业趋势与群像剧创作，负责行业观察类内容。" },
  { name: "沉木", role: "角色分析师", desc: "专注人物动机与情感弧光，负责角色专题与细节考据。" },
  { name: "编辑部", role: "资讯与运营", desc: "负责每日资讯更新、选题策划与读者反馈跟进。" }
];

export default function About() {
  useDocumentTitle(
    "关于我们 - 亲吻姐姐影视剧资讯与剧情解析",
    "了解亲吻姐姐的网站定位、内容范围与团队介绍。我们专注影视剧资讯、剧情介绍与角色分析，帮助观众深入理解影视作品。"
  );

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-14 lg:px-8">
          <Breadcrumb items={[{ label: "关于我们" }]} />
          <h1 className="mt-5 font-heading text-3xl font-extrabold text-foreground-950 md:text-4xl">
            关于亲吻姐姐
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            亲吻姐姐是一家聚焦影视剧最新资讯、剧情解析和角色剖析的专业影视内容站。我们相信，一部好的影视作品值得被认真对待，
            而认真对待的第一步，就是把它讲清楚。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6 md:py-16 lg:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-2xl font-extrabold text-foreground-950">网站定位与内容范围</h2>
            <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
              我们的目标用户是喜欢电视剧、电影、网络剧的观众，以及对剧情深度感兴趣的爱好者。无论你是想快速了解一部剧讲了什么，
              还是想研究某个角色的动机为何如此，这里都能提供清晰的答案。
            </p>
            <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
              内容范围覆盖中国大陆、中国香港、中国台湾、日本、韩国、美国、英国等地区的影视作品，题材从古装权谋、都市情感，
              到悬疑犯罪、科幻奇幻与青春成长，均有涉猎。
            </p>

            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
              {SCOPE.map((item) => (
                <div key={item.title} className="rounded-xl border border-background-200 bg-background-50 p-6">
                  <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>

            <h2 className="mt-12 font-heading text-2xl font-extrabold text-foreground-950">团队介绍</h2>
            <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
              亲吻姐姐由一支小而专注的内容团队运营，成员来自影视评论、媒体与编辑领域，长期关注剧集叙事与人物塑造。
            </p>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {TEAM.map((member) => (
                <div key={member.name} className="flex gap-4 rounded-xl border border-background-200 bg-background-50 p-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-secondary-100 font-heading text-lg font-bold text-secondary-800">
                    {member.name.slice(0, 1)}
                  </span>
                  <div className="min-w-0">
                    <p className="font-heading text-base font-bold text-foreground-950">{member.name}</p>
                    <p className="text-xs text-accent-600">{member.role}</p>
                    <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{member.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-xl border border-background-200 bg-background-50 p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-shield-check-line text-primary-600"></i>
                版权声明
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                本站所有影视作品名称、剧情介绍、角色设定与配图，除特别注明外，均为演示示例内容，人物与情节为虚构创作，
                不指向任何真实的影视作品或公众人物。
              </p>
            </div>
            <div className="mt-5 rounded-xl border border-background-200 bg-background-50 p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-file-text-line text-accent-600"></i>
                法律声明
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                本站内容仅用于信息展示与学习交流。用户在使用本站内容时应遵守相关法律法规，不得用于任何商业用途。
                如对内容有异议，欢迎通过联系反馈页与我们沟通。
              </p>
            </div>
            <div className="mt-5 rounded-xl bg-primary-900 p-6 text-background-100">
              <h2 className="font-heading text-lg font-bold text-background-50">有内容想推荐？</h2>
              <p className="mt-2 text-sm leading-relaxed text-primary-100/90">
                欢迎向我们投稿影视资讯、剧情解析或角色分析，一起把好作品讲清楚。
              </p>
              <Link
                to="/contact"
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
              >
                <i className="ri-mail-send-line"></i>
                前往联系反馈
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}